/**
 * Backend istemcisi.
 *
 * Tum istekler `/api` altina gider ve Vite proxy'si uzerinden
 * 127.0.0.1:8000'e yonlenir. Hata durumunda backend'in Turkce `detail`
 * mesaji korunur - kullaniciya ham HTTP kodu gosterilmez.
 */

import type {
  AdminDashboard,
  AdminUser,
  Analysis,
  Announcement,
  AuditEntry,
  AuthUser,
  Candles,
  Health,
  IntradayReport,
  JournalStats,
  LiquidityInfo,
  MarketOverview,
  OrderResponse,
  PendingScan,
  Portfolio,
  PriceAlert,
  QuickMoveHistory,
  QuickMoveScan,
  QuickMoveStatus,
  ReferralCode,
  ScanResult,
  Settings,
  SignalDetail,
  SignalHistory,
  SignalScan,
  TradesResponse,
  TradingModeSetting,
  TechnicalReport,
} from "./types";

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const REQUEST_TIMEOUT_MS = 20_000;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  // Takilan istek (ag degisti, telefon uyudu) sonraki yenilemeleri bekletmesin: 20 sn sinir.
  // no-store: tarayici/iPhone eski cevabi onbellekten vermesin (kullanici 11.10).
  const timeout = init?.signal ? null : new AbortController();
  const timer = timeout
    ? window.setTimeout(() => timeout.abort(), REQUEST_TIMEOUT_MS)
    : 0;
  try {
    response = await fetch(`/api${path}`, {
      cache: "no-store",
      ...init,
      signal: init?.signal ?? timeout?.signal,
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch {
    window.clearTimeout(timer);
    throw new ApiError(
      "Sunucuya ulasilamadi. Backend calisiyor mu? (csb serve)",
      0,
    );
  }

  if (!response.ok) {
    let detail = `Sunucu hatasi (${response.status})`;
    try {
      const payload = (await response.json()) as { detail?: unknown };
      if (typeof payload.detail === "string") {
        detail = payload.detail;
      } else if (Array.isArray(payload.detail)) {
        detail = "Gecersiz istek: " + JSON.stringify(payload.detail);
      }
    } catch {
      /* govde JSON degilse varsayilan mesaj kalir */
    }
    window.clearTimeout(timer);
    throw new ApiError(detail, response.status);
  }

  try {
    return (await response.json()) as T;
  } finally {
    window.clearTimeout(timer);
  }
}

export const api = {
  health: () => request<Health>("/health"),

  /** Kayit/giris/cikis - bkz. backend auth/__init__.py. */
  auth: {
    me: () => request<AuthUser>("/auth/me"),
    register: (input: {
      firstName: string;
      lastName: string;
      phone: string;
      email: string;
      password: string;
      referralCode: string;
    }) =>
      request<AuthUser>("/auth/register", {
        method: "POST",
        body: JSON.stringify({
          first_name: input.firstName,
          last_name: input.lastName,
          phone: input.phone,
          email: input.email,
          password: input.password,
          referral_code: input.referralCode,
        }),
      }),
    login: (email: string, password: string) =>
      request<AuthUser>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }),
    logout: () =>
      request<{ logged_out: boolean }>("/auth/logout", { method: "POST" }),
    /** Kullanicinin KENDI sifresini degistirmesi. Mevcut sifre her zaman
     *  istenir - acik unutulmus bir oturumu ele geciren biri hesabi
     *  kalici olarak alamasin diye. */
    changePassword: (currentPassword: string, newPassword: string) =>
      request<AuthUser>("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      }),
  },

  /** Yonetici paneli (Dashboard/Users/Referral Codes) - yalnizca admin. */
  admin: {
    dashboard: () => request<AdminDashboard>("/admin/dashboard"),
    /** Vadeli > Gun ici LONG firsatlari - bilgi amacli, yalniz yonetici. */
    intraday: () => request<IntradayReport>("/admin/intraday"),
    intradayNotify: (enabled: boolean) =>
      request<{ enabled: boolean }>("/admin/intraday/notify", {
        method: "PUT",
        body: JSON.stringify({ enabled }),
      }),
    users: () => request<AdminUser[]>("/admin/users"),
    suspendUser: (userId: string) =>
      request<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/suspend`, {
        method: "POST",
      }),
    reactivateUser: (userId: string) =>
      request<AdminUser>(
        `/admin/users/${encodeURIComponent(userId)}/reactivate`,
        {
          method: "POST",
        },
      ),
    /** Tek kullanimlik gecici sifre uretir. Sifre YALNIZCA bu yanitta
     *  doner - saklanmaz; pencere kapanirsa yeniden sifirlamak gerekir. */
    resetUserPassword: (userId: string) =>
      request<{ user_id: string; email: string; temporary_password: string }>(
        `/admin/users/${encodeURIComponent(userId)}/reset-password`,
        { method: "POST" },
      ),
    referralCodes: () => request<ReferralCode[]>("/admin/referral-codes"),
    createReferralCode: (expiresAt?: number) =>
      request<ReferralCode>(
        `/admin/referral-codes${expiresAt ? `?expires_at=${expiresAt}` : ""}`,
        { method: "POST" },
      ),
    cancelReferralCode: (code: string) =>
      request<{ cancelled: boolean }>(
        `/admin/referral-codes/${encodeURIComponent(code)}`,
        {
          method: "DELETE",
        },
      ),
    /** Tum KULLANILMAMIS kodlari topluca temizler - KULLANILMIS kodlara dokunmaz. */
    purgeUnusedReferralCodes: () =>
      request<{ removed: number }>("/admin/referral-codes/purge-unused", {
        method: "POST",
      }),
    /** Duyuru panosunu gunceller - bos metin duyuruyu KALDIRIR. */
    updateAnnouncement: (text: string) =>
      request<Announcement>("/admin/announcement", {
        method: "PUT",
        body: JSON.stringify({ text }),
      }),
    /** Sabit Bilgi panosunu gunceller - Duyuru'dan AYRI, kalici bir not alani. */
    updatePinnedNote: (text: string) =>
      request<Announcement>("/admin/pinned-note", {
        method: "PUT",
        body: JSON.stringify({ text }),
      }),
  },

  /** Admin'in yayinladigi duyuru - aktif her uye gorur. */
  announcement: () => request<Announcement>("/announcement"),

  /** Sabit Bilgi - Duyuru'dan AYRI, kalici bir not alani. */
  pinnedNote: () => request<Announcement>("/pinned-note"),

  runScan: () => request<ScanResult>("/scan", { method: "POST" }),
  lastScan: () => request<ScanResult>("/scan"),

  analysis: (symbol: string) =>
    request<Analysis>(`/symbols/${encodeURIComponent(symbol)}/analysis`),

  /** Teknik analiz raporu (Analiz sekmesi) - yon tahmini URETMEZ. */
  technical: (symbol: string) =>
    request<TechnicalReport>(`/symbols/${encodeURIComponent(symbol)}/technical`),

  candles: (symbol: string, timeframe: string, limit = 300) =>
    request<Candles>(
      `/symbols/${encodeURIComponent(symbol)}/candles?tf=${timeframe}&limit=${limit}`,
    ),

  /** Onaylanmis sinyaller (UT Bot, yalnizca kapanmis barlar). */
  signals: (
    timeframe: string,
    options: {
      side?: "all" | "buy" | "sell";
      favoritesOnly?: boolean;
      refresh?: boolean;
    } = {},
  ) => {
    // Backend varsayilani 200'dur; evren (USDT spot) 493 pariteyi asamaz,
    // bu yuzden ucun tavani olan 500 TUM sinyalleri kapsar. Limit
    // gonderilmezse liste SESSIZCE 200 satirda kesilirdi.
    const query = new URLSearchParams({ tf: timeframe, limit: "500" });
    if (options.side && options.side !== "all") query.set("side", options.side);
    if (options.favoritesOnly) query.set("favorites_only", "true");
    if (options.refresh) query.set("refresh", "true");
    return request<SignalScan>(`/signals?${query.toString()}`);
  },

  signalHistory: (timeframe: string, symbol?: string, limit = 100) => {
    const query = new URLSearchParams({ tf: timeframe, limit: String(limit) });
    if (symbol) query.set("symbol", symbol);
    return request<SignalHistory>(`/signals/history?${query.toString()}`);
  },

  /** Onay bekleyen adaylar - YALNIZCA YONETICI. Onaylanmis sinyal DEGIL. */
  pendingSignals: (timeframe: string) =>
    request<PendingScan>(`/signals/pending?tf=${timeframe}`),


  /** Tum zamanlar ozeti - kalici gunlukten, 500 satir sinirindan bagimsiz. */
  journalStats: () => request<JournalStats>("/signals/journal-stats"),

  signalDetail: (symbol: string, timeframe: string) =>
    request<SignalDetail>(
      `/symbols/${encodeURIComponent(symbol)}/signal?tf=${timeframe}`,
    ),


  /**
   * Quick-Move - DENEYSEL gozlem taramasi. AL/SAT sinyalinden TAMAMEN
   * AYRIDIR, yon bildirmez (bkz. types.ts QuickMoveScan aciklamasi).
   */
  quickMove: () => request<QuickMoveScan>("/quickmove"),

  /** Ust serit + one cikanlar - taranan evrenin 24s ozeti (yeni istek yok). */
  marketOverview: () => request<MarketOverview>("/market/overview"),

  /**
   * Quick-Move "guclu tetiklenme" GECMISI - anlik degil, KALICI (bkz.
   * types.ts QuickMoveHistory aciklamasi).
   */
  quickMoveHistory: (hours = 48) =>
    request<QuickMoveHistory>(`/quickmove/history?hours=${hours}`),

  /**
   * Quick-Move ANLIK durumu - periyodik Top-K taramasindan BAGIMSIZ,
   * SECILEN herhangi bir sembol icin (bkz. types.ts QuickMoveStatus).
   */
  quickMoveStatus: (symbol: string) =>
    request<QuickMoveStatus>(`/quickmove/status/${encodeURIComponent(symbol)}`),

  /**
   * Fiyat & Sinyal Alarmlari - sunucu tarafinda kalici (bkz. types.ts
   * PriceAlert aciklamasi). Eskiden yalnizca localStorage'daydi.
   */
  /** Telefon bildirimleri (Web Push) - bkz. lib/push.ts. */
  pushConfig: () =>
    request<{ public_key: string; devices: number }>("/push/config"),
  pushSubscribe: (body: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    device: string;
  }) =>
    request<{ devices: number }>("/push/subscribe", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  pushUnsubscribe: (endpoint: string) =>
    request<{ removed: boolean; devices: number }>("/push/unsubscribe", {
      method: "POST",
      body: JSON.stringify({ endpoint }),
    }),
  pushTest: () =>
    request<{ sent: number; failed: number; devices: number }>("/push/test", {
      method: "POST",
    }),
  /** Emir defterinden gercek islem maliyeti (bkz. LiquidityInfo). */
  liquidity: (symbol: string, size = 1000, timeframe?: string) => {
    const query = new URLSearchParams({ size: String(size) });
    if (timeframe) query.set("tf", timeframe);
    return request<LiquidityInfo>(
      `/symbols/${encodeURIComponent(symbol)}/liquidity?${query.toString()}`,
    );
  },
  alerts: () => request<PriceAlert[]>("/alerts"),
  createAlert: (input: {
    symbol: string;
    sellTimeframes: string[];
    priceTarget: number | null;
  }) =>
    request<PriceAlert>("/alerts", {
      method: "POST",
      body: JSON.stringify({
        symbol: input.symbol,
        sell_timeframes: input.sellTimeframes,
        price_target: input.priceTarget,
      }),
    }),
  removeAlert: (id: string) =>
    request<{ removed: boolean }>(`/alerts/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  toggleFavorite: (symbol: string) =>
    request<{ favorites: string[]; is_favorite: boolean }>(
      `/favorites/${encodeURIComponent(symbol)}`,
      { method: "POST" },
    ),

  positions: () => request<Portfolio>("/positions"),
  trades: () => request<TradesResponse>("/trades"),

  buy: (symbol: string, quoteAmount?: number) =>
    request<OrderResponse>("/orders", {
      method: "POST",
      body: JSON.stringify({
        symbol,
        side: "BUY",
        quote_amount: quoteAmount ?? null,
      }),
    }),

  closePosition: (symbol: string) =>
    request<OrderResponse>(`/positions/${encodeURIComponent(symbol)}/close`, {
      method: "POST",
    }),

  settings: () => request<Settings>("/settings"),
  updateSettings: (body: Record<string, unknown>) =>
    request<Settings>("/settings", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  setMode: (mode: TradingModeSetting) =>
    request<{ trading_mode: TradingModeSetting }>("/mode", {
      method: "POST",
      body: JSON.stringify({ mode }),
    }),

  emergencyStop: () =>
    request<{
      safe_mode: boolean;
      trading_mode: TradingModeSetting;
      message: string;
    }>("/emergency-stop", { method: "POST" }),

  clearSafeMode: () =>
    request<{ safe_mode: boolean; trading_mode: TradingModeSetting }>(
      "/safe-mode/clear",
      { method: "POST" },
    ),

  audit: (limit = 120) =>
    request<{ entries: AuditEntry[] }>(`/audit?limit=${limit}`),
};
